/**
 * Whether the app is running as an installed app, and telling the backend so.
 *
 * The backend uses this to give an installed session a 30-day window instead of 7
 * (`docs/AuthSessions.md` §3). Three things about that are worth knowing before changing
 * anything here:
 *
 *  - **It rides every request, not just login.** On desktop an installed PWA shares its
 *    cookie jar with the browser, so a user who installs already holds a valid session and
 *    may not sign in again for weeks. Asserting only at login would make the longer window
 *    invisible on exactly the surface that introduced it.
 *  - **It is an assertion, not proof.** Any client can send the header. It grants nothing
 *    an attacker with valid credentials does not already have — it is "remember me",
 *    spelled as an environment check.
 *  - **Absence means nothing.** The server ratchets the lifetime upward only, so an
 *    ordinary tab sharing that one cookie never shortens what the installed app raised.
 */

/** Must match `user_sessions::DISPLAY_MODE_HEADER` in website-backend. */
export const DISPLAY_MODE_HEADER = 'x-app-display-mode';

/** Must match `DISPLAY_MODE_STANDALONE` in website-backend. */
const STANDALONE = 'standalone';

/**
 * Is this window an installed app?
 *
 * Two checks because the platforms disagree: `display-mode: standalone` is the standard
 * and what Chrome/Edge/Android report, while iOS Safari has long exposed the non-standard
 * `navigator.standalone` instead.
 */
export function isStandalone(): boolean {
  if (matchesStandalone()) return true;
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * `matchMedia` is typed as always present but is genuinely absent in some environments
 * (jsdom, for one), so this checks for the function rather than optional-chaining a value
 * the type system insists cannot be null.
 */
function matchesStandalone(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * The shape of `axios.defaults` this needs, described structurally rather than imported.
 *
 * `unknown` values because axios types a header as `AxiosHeaderValue` (string | number |
 * boolean | null), and narrowing to `string` here would reject the very object this is
 * meant to take. Keeping axios out of the imports also keeps this module testable without
 * it.
 */
export interface AxiosDefaultsLike {
  headers: { common: Record<string, unknown> };
}

/**
 * Keep the header on the shared axios defaults in sync with the display mode.
 *
 * Deliberately on `axios.defaults` rather than an interceptor: the app calls the bare
 * `axios` export everywhere, so defaults are the only place that reaches every request.
 * The service worker's own `fetch` calls are unaffected, which is correct — a push
 * re-subscription is not evidence about how the user is running the app.
 *
 * Returns a cleanup function for the media-query subscription.
 */
export function syncDisplayModeHeader(axiosDefaults: AxiosDefaultsLike): () => void {
  const apply = (standalone: boolean) => {
    if (standalone) {
      axiosDefaults.headers.common[DISPLAY_MODE_HEADER] = STANDALONE;
    } else {
      // Removing rather than sending a "browser" value: the server treats absence as "no
      // assertion", and a value it does not recognise would be indistinguishable from a
      // typo. Nothing here should ever be able to shorten a session.
      Reflect.deleteProperty(axiosDefaults.headers.common, DISPLAY_MODE_HEADER);
    }
  };

  apply(isStandalone());

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;

  // A window can transition — installing from a tab on desktop hands the same page to an
  // app window in some browsers — so this is not purely a startup concern.
  const query = window.matchMedia('(display-mode: standalone)');
  const onChange = (event: MediaQueryListEvent) => { apply(event.matches || isStandalone()); };
  query.addEventListener('change', onChange);
  return () => { query.removeEventListener('change', onChange); };
}
